import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type NavItem, Page } from '../../../components';
import type { CdnLocation } from '../types';
import { postCdnLocation, useOnMessage } from '../utils/messages';
import { getPulseCdnIframeUrl } from '../utils/path.ts';
import classes from './PulseCdnPage.module.css';

const PAGE_ROUTE = '/pulsecdn';

export default observer(function PulseCdnPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const path = useRoutePath();
  const initialPath = useRef(path);
  const cdnPath = useRef(path);
  const navigate = useNavigate();

  useOnMessage<CdnLocation>('cdnOnLocationChange', (location) => {
    if (location.path !== cdnPath.current) {
      cdnPath.current = location.path;
      navigate(PAGE_ROUTE + encodePath(location.path));
    }
  });

  useEffect(() => {
    if (path !== cdnPath.current) {
      cdnPath.current = path;
      postCdnLocation(iframeRef.current, path);
    }
  }, [path]);

  return (
    <Page navigation={pathToNavigation(path)}>
      <iframe
        className={classes.frame}
        ref={iframeRef}
        src={getPulseCdnIframeUrl(encodePath(initialPath.current), {
          showRefreshButton: true,
          showSelectorModeHints: false,
        })}
        title="PulseCDN"
      />
    </Page>
  );
});

function pathToNavigation(path: string): NavItem[] {
  const names = path.split('/').filter(Boolean);

  return names.map((name, index) => ({
    route:
      index === names.length - 1
        ? undefined
        : PAGE_ROUTE + encodePath(`/${names.slice(0, index + 1).join('/')}`),
    title: name,
  }));
}

function useRoutePath(): string {
  const { pathname } = useLocation();
  const path = decodeURIComponent(pathname.slice(PAGE_ROUTE.length));
  return path.length > 1 ? path.replace(/\/+$/, '') : '/';
}

function encodePath(path: string): string {
  return path
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
}
